import { Box, Typography, Theme, CircularProgress } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useEffect, useState } from "react";
import CustomButton from "../../components/CustomButton";
import { showFlashMessageWithTimeout } from "../../services/actions/flashMessage-actions";
import { UserManager } from "../../services/api/UserManager";
import { useDispatch, useSelector } from "react-redux";
import { UserDto } from "../../shared/types/userTypes";
import { translatePeriodOfTime } from "../../utils/subscrtiption-utils";
import { setUserInfo } from "../../services/actions/user-actions";
import CancelSubscriptionModal from "../../components/CancelSubscriptionModal";
import ChangeSubscriptionModal from "../../components/ChangeSubscriptionModal";
import { PricingPlanPeriod } from "../../shared/types/pricingPlanTypes";
import { getAdminInfo } from "../../services/selectors/user-selectors";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Routes } from "../../navigation/navigation-types";
import {
  PricingPlanUsa,
  PricingPlanTypeUsa,
  yearPricingPlansUsa,
  monthPricingPlansUsa
} from "./components/pricingPlansUsa";
import PlanCardUsa from "./components/PlanCardUsa";
import SubscriptionSection from "./components/SubscriptionSection";
import PeriodSelector from "./components/PeriodSelector";

interface ProfileSubscriptionUsaProps {
  user: UserDto | undefined;
  onUpdateUser: (user: UserDto) => void;
}

const ProfileSubscriptionUsa: React.FC<ProfileSubscriptionUsaProps> = ({
  user,
  onUpdateUser,
}: ProfileSubscriptionUsaProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const scrollToId = (location as any).state?.scrollToId;

  const [newSelectedPlan, setNewSelectedPlan] = useState<PricingPlanUsa>();
  const [planList, setPlanList] = useState<PricingPlanUsa[]>([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState<boolean>(true);

  const [selectedSubscriptionType, setSelectedSubscriptionType] =
    useState<PricingPlanPeriod>(
      user?.pricingPlanPeriod ? user.pricingPlanPeriod : "year",
    );

  const [isSubscriptionCanceling, setIsSubscriptionCanceling] =
    useState<boolean>(false);
  const [isSubscriptionLoading, setIsSubscriptionLoading] =
    useState<boolean>(false);

  const [isCancelSubscriptionModalOpen, setIsCancelSubscriptionModalOpen] =
    useState<boolean>(false);
  const [isChangeSubscriptionModalOpen, setIsChangeSubscriptionModalOpen] =
    useState<boolean>(false);

  const navigate = useNavigate();

  const canUserBuyExtraCredits =
    user && ["lite", "medium"].includes(user.pricingPlanName?.toLowerCase() ?? "");

  const adminInfo = useSelector(getAdminInfo);
  const disableActions = adminInfo.id.length > 0;

  const CreateStripeSubscriptionCheckout = async (
    priceId: string,
    pricingPlanName: PricingPlanTypeUsa,
    period: PricingPlanPeriod,
    numberOfNotifications: number,
  ) => {
    try {
      if (disableActions) {
        showFlashMessageWithTimeout(
          dispatch,
          t("popups.changeSubscriptionDisabled"),
          "error",
        );
        return;
      }
      setIsSubscriptionLoading(true);
      if (!user) throw new Error("Uživatel nenalezen");
      const checkoutRes =
        await UserManager.getManager().subscribeByPriceId(priceId);
      if (checkoutRes.sessionUrl) {
        window.location.href = checkoutRes.sessionUrl;
      }
      if (checkoutRes.success) {
        onUpdateUser({
          ...user,
          lastPricingPlanName: user.pricingPlanName,
          isInTrial: false,
          pricingPlanName: pricingPlanName,
          pricingPlanPeriod: period,
          maxPricingPlanCredits: numberOfNotifications,
          remainingPricingPlanCredits: numberOfNotifications,
          lastPricingPlanPeriodStartsAt: new Date(),
          lastPricingPlanPeriodEndsAt:
            period === "month"
              ? new Date(new Date().setMonth(new Date().getMonth() + 1))
              : new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
        });
        setIsChangeSubscriptionModalOpen(false);
        showFlashMessageWithTimeout(
          dispatch,
          t("popups.subscriptionChangedTo") + pricingPlanName.toUpperCase(),
          "success",
        );
      }
    } catch (err: any) {
      setIsSubscriptionLoading(false);
      return showFlashMessageWithTimeout(dispatch, err.toString(), "error");
    }
    setIsSubscriptionLoading(false);
    dispatch(setUserInfo(user));
  };

  const CancelStripeSubscription = async () => {
    try {
      if (disableActions) {
        showFlashMessageWithTimeout(
          dispatch,
          t("popups.changeSubscriptionDisabled"),
          "error",
        );
        return;
      }
      setIsSubscriptionCanceling(true);
      if (!user) throw new Error("Uživatel nenalezen");
      await UserManager.getManager().cancelStripeSubscription();
      onUpdateUser({
        ...user,
        lastPricingPlanName: user?.pricingPlanName,
        pricingPlanName: undefined,
      });
    } catch (err: any) {
      setIsSubscriptionCanceling(false);
      return showFlashMessageWithTimeout(dispatch, err.toString(), "error");
    }
    setIsSubscriptionCanceling(false);
    setIsCancelSubscriptionModalOpen(false);
    dispatch(setUserInfo(user));
    showFlashMessageWithTimeout(
      dispatch,
      t("popups.subscriptionCanceled"),
      "success",
    );
  };

  const HandleOpenChangeSubscriptionModal = (plan: PricingPlanUsa) => {
    setNewSelectedPlan(plan);
    setIsChangeSubscriptionModalOpen(true);
  };

  useEffect(() => {
    if (scrollToId) {
      setTimeout(() => {
        const element = document.getElementById(scrollToId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 0);
    }
  }, [scrollToId]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoadingProducts(true);
        // Fetch only USD products for USA version
        const productsData = await UserManager.getManager().getStripeProductsGrouped("usd");

        // Combine year and month plans from hardcoded data
        const allHardcodedPlans = [...yearPricingPlansUsa, ...monthPricingPlansUsa];

        // Map through hardcoded plans and add priceId from API
        const plansWithPriceIds: PricingPlanUsa[] = allHardcodedPlans.map((hardcodedPlan) => {
          // Find matching product in API data using stripeProductName from the plan
          const matchingProduct = productsData.subscriptions.products.find(
            (product: any) => product.name === hardcodedPlan.stripeProductName
          );

          if (matchingProduct) {
            // Find matching price for the period
            const matchingPrice = matchingProduct.prices.find(
              (price: any) => {
                const apiPeriod = price.recurring?.interval === "month" ? "month" : "year";
                return apiPeriod === hardcodedPlan.periodOfTime;
              }
            );

            if (matchingPrice) {
              return {
                ...hardcodedPlan,
                priceId: matchingPrice.id,
                apiPrice: matchingPrice.unit_amount, // Price in cents from API
              };
            }
          }

          // If no match found, return original plan without priceId and apiPrice
          return hardcodedPlan;
        });

        setPlanList(plansWithPriceIds);
      } catch (error) {
        console.error("Failed to fetch products:", error);
        showFlashMessageWithTimeout(
          dispatch,
          "Failed to load pricing plans",
          "error"
        );
        // Fallback to hardcoded plans without priceIds
        setPlanList([...yearPricingPlansUsa, ...monthPricingPlansUsa]);
      } finally {
        setIsLoadingProducts(false);
      }
    };

    fetchProducts();
  }, [dispatch]);

  if (!user) {
    return <Typography variant='h5'>{t("userIsNotLoggedIn")}</Typography>;
  }

  const getMonthlyCreditsText = () => {
    if (!user.isInTrial && user.nextCreditsChargedAt) {
      return (
        " " +
        t("subscriptions.tarifs.creditsRefresh") +
        " " +
        new Date(user.nextCreditsChargedAt).toLocaleDateString("cs-CS")
      );
    }
    if (!user.nextCreditsChargedAt && user.lastPricingPlanPeriodEndsAt) {
      return (
        t("subscriptions.tarifs.creditsExpire") +
        " " +
        new Date(user.lastPricingPlanPeriodEndsAt).toLocaleDateString("cs-CS") +
        " (" +
        user.lastPricingPlanName +
        ")"
      );
    }
    return "";
  };

  return (
    <Box style={{ flex: 1 }}>
      <Box className={classes.container}>
        <Typography className={classes.titleText}>
          {t("subscriptions.subTitle")}
        </Typography>
        <SubscriptionSection
          title={t("monthlyCredits") ?? ""}
          mainValue={
            user.isInTrial
              ? t("subscriptions.tarifs.trial") ?? ""
              : `${user?.remainingPricingPlanCredits} / ${
                  user?.maxPricingPlanCredits ?? 0
                }`
          }
          secondaryText={getMonthlyCreditsText()}
          showProgress={!user.isInTrial}
          progressPercent={
            user?.maxPricingPlanCredits
              ? (user?.remainingPricingPlanCredits / user?.maxPricingPlanCredits) *
                100
              : 0
          }
          isItalic={user.isInTrial}
        />
        <SubscriptionSection
          title={t("extraCredits") ?? ""}
          mainValue={user?.remainingExtraCredits ?? 0}
          secondaryText={t("subscriptions.portableCreditsDescription") ?? ""}
        >
          {canUserBuyExtraCredits && (
            <Box
              sx={{
                width: 1 / 4,
                pt: 2,
              }}
            >
              <CustomButton
                onClick={() =>
                  navigate(Routes.extraCredits, {
                    state: { user },
                  })
                }
                title={t("extraCreditsPage.navigationButtonText")}
                size='medium'
              />
            </Box>
          )}
        </SubscriptionSection>
        {user.pricingPlanName && (
          <Box className={classes.sectionContainer}>
            <Box className={classes.upperSectionContainer}>
              <Typography className={classes.mainText}>
                {t("subscriptions.tarifs.subbscription")}{" "}
                {user?.isInTrial &&
                  t("subscriptions.tarifs.trialVersion") + " "}
                {user.pricingPlanName &&
                  user.pricingPlanPeriod &&
                  user.pricingPlanName +
                    " " +
                    translatePeriodOfTime(user.pricingPlanPeriod)}
              </Typography>
              <Typography
                className={classes.coloredText}
                onClick={() => setIsCancelSubscriptionModalOpen(true)}
              >
                {t("subscriptions.tarifs.cancelSubscription")}
              </Typography>
              <CancelSubscriptionModal
                isOpen={isCancelSubscriptionModalOpen}
                onClose={() => setIsCancelSubscriptionModalOpen(false)}
                onCancelSubscription={CancelStripeSubscription}
                isLoading={isSubscriptionCanceling}
              />
            </Box>
            <Box className={classes.sectionDivider} />
            {!user.hasUnlimitedAccess && (
              <Typography className={classes.secondaryText}>
                {user?.isInTrial
                  ? `${t("subscriptions.tarifs.creditsExpire")} `
                  : `${t("subscriptions.tarifs.creditsExpire")} `}
                {new Date(
                  user?.lastPricingPlanPeriodEndsAt ?? "",
                ).toLocaleDateString("cs-CS")}
              </Typography>
            )}
            {user.hasUnlimitedAccess && (
              <Typography className={classes.secondaryText}>
                {t("subscriptions.tarifs.unlimitedVersion")}
              </Typography>
            )}
          </Box>
        )}
        <Typography className={classes.titleText} style={{ marginTop: 40 }}>
          {t("subscriptions.tarifs.title")}
        </Typography>
        <PeriodSelector
          selectedPeriod={selectedSubscriptionType}
          onPeriodChange={setSelectedSubscriptionType}
        />
        <Box
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "row",
            marginTop: 32,
            marginBottom: 24,
          }}
        >
          {isLoadingProducts ? (
            <Box
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 400,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            planList
              .filter((plan) => plan.periodOfTime === selectedSubscriptionType)
              .map((plan, index) => {
            const isSelected =
              user.pricingPlanName?.toLowerCase() === plan.type.toLowerCase() &&
              user.pricingPlanPeriod?.toLowerCase() === plan.periodOfTime;
            const isFavorite = plan.type === "UNLIMITED";
            const buttonText =
              user.lastPricingPlanName || user.pricingPlanName
                ? t("subscriptions.tarifs.choseSubscription")
                : t("subscriptions.tarifs.activateFreeTrial");

            const handleButtonClick = () => {
              if (!plan.priceId) {
                showFlashMessageWithTimeout(
                  dispatch,
                  `Plan ${plan.type} is not available. Please contact support.`,
                  "error"
                );
                return;
              }

              if (user.pricingPlanName) {
                HandleOpenChangeSubscriptionModal(plan);
              } else {
                CreateStripeSubscriptionCheckout(
                  plan.priceId,
                  plan.type,
                  plan.periodOfTime,
                  plan.numberOfNotifications,
                );
              }
            };

            return (
              <PlanCardUsa
                key={index}
                plan={plan}
                isSelected={isSelected}
                isFavorite={isFavorite}
                isLoading={isSubscriptionLoading || !plan.priceId}
                buttonText={!plan.priceId ? t("subscriptions.tarifs.notAvailable") || "Not available" : buttonText}
                onButtonClick={handleButtonClick}
              />
            );
          })
          )}
        </Box>
        <ChangeSubscriptionModal
          isOpen={isChangeSubscriptionModalOpen}
          onClose={() => setIsChangeSubscriptionModalOpen(false)}
          oldSubscription={user.pricingPlanName}
          newSubscription={newSelectedPlan?.type}
          onChangeSubscription={() => {
            if (newSelectedPlan && newSelectedPlan.priceId) {
              CreateStripeSubscriptionCheckout(
                newSelectedPlan.priceId,
                newSelectedPlan.type,
                newSelectedPlan.periodOfTime,
                newSelectedPlan.numberOfNotifications,
              );
            } else {
              showFlashMessageWithTimeout(
                dispatch,
                `Plan ${newSelectedPlan?.type} is not available. Please contact support.`,
                "error"
              );
            }
          }}
          isLoading={isSubscriptionLoading}
        />
        <Box className={classes.footerTextContainer}>
          <Typography className={classes.footerTopText}>
            {t("subscriptions.tarifs.pricesAreFinal")}
          </Typography>
          <Box className={classes.footerHintContainer}>
            <Typography className={classes.footerTopText}>
              {t("subscriptions.tarifs.moreViews")}
            </Typography>
            <button
              onClick={() => {
                const tawkAPI = (window as any).Tawk_API;
                if (typeof window !== 'undefined' && tawkAPI && tawkAPI._isLoaded && tawkAPI.toggle) {
                  tawkAPI.toggle();
                } else {
                  console.warn('Tawk.to widget is not loaded yet. Please try again in a moment.');
                }
              }}
              style={{
                cursor: "pointer",
                background: "none",
                border: "none",
                padding: 0,
                font: "inherit",
              }}
            >
              <Typography className={classes.footerBottomText}>
                {t("subscriptions.tarifs.contactUsForCustom")}
              </Typography>
            </button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    marginTop: 24,
  },
  sectionDivider: {
    height: 2,
    width: "100%",
    backgroundColor: theme.palette.natural.t8,
    marginTop: 8,
    marginBottom: 8,
  },
  upperSectionContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  sectionContainer: {
    display: "flex",
    flexDirection: "column",
    maxWidth: 564,
    marginTop: 40,
  },
  coloredText: {
    color: theme.palette.main.primary.o,
    fontSize: 14,
    fontWeight: "bold",
    cursor: "pointer",
  },
  mainText: {
    fontSize: 16,
    fontWeight: "bold",
    color: theme.palette.natural.t1,
  },
  secondaryText: {
    fontSize: 14,
    color: theme.palette.natural.t1,
  },
  titleText: {
    fontSize: 22,
    fontWeight: "bold",
    color: theme.palette.main.secondary.u1,
  },
  footerTextContainer: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  footerTopText: {
    textAlign: "center",
    fontSize: 16,
    fontWeight: "400",
    color: theme.palette.natural.t1,
    marginTop: 2,
  },
  footerHintContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    marginTop: 20,
    marginBottom: 40,
  },
  footerBottomText: {
    fontSize: 18,
    fontWeight: "700",
    color: theme.palette.main.primary.o,
    marginLeft: 4,
  },
}));

export default ProfileSubscriptionUsa;
